"use client";

import type { WorkspacesResponse } from "@/features/workspaces/types/workspaceWidget";

import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { useNavigate, useParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import useUserStore from "@/features/auth/store/useUserStore";
import PATHS from "@/routes/paths";

export const WorkspacesList = () => {
  const { t } = useTranslation(["workspaces", "common"]);
  const [workspaces, setWorkspaces] = useState<WorkspacesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useUserStore();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const isTutor = user?.role === "tutor";

  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        const { data } = await axiosApiInstance.get<WorkspacesResponse>(
          API_ENDPOINTS.WORKSPACES.WIDGET,
        );

        setWorkspaces(data);
      } catch (error) {
        console.error("Error fetching workspaces:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkspaces();
  }, []);

  const handleWorkspaceClick = (id?: string) => {
    const path = isTutor
      ? `${PATHS.TUTOR.WORKSPACES.WORKSPACES}/${id || "main"}`
      : `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/${id || "main"}`;

    navigate(path);
  };

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="bg-foreground/50 h-fit rounded-2xl border-none p-4 backdrop-blur-xl sm:p-6"
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay: 0.2 }}
    >
      <div className="mb-3 flex items-center justify-between sm:mb-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <Icon
            className="text-accent-foreground"
            height="1.25rem"
            icon="mdi:folder-multiple-outline"
            width="1.25rem"
          />
          <h3 className="text-accent-foreground text-base font-semibold sm:text-lg">
            {t("workspaces:dashboard.workspacesList")}
          </h3>
        </div>
        <Badge
          className="bg-muted/30 text-muted-foreground border-border/50 text-xs"
          variant="outline"
        >
          {workspaces?.total_workspaces_count || 0}
        </Badge>
      </div>

      <ScrollArea className="h-60 sm:h-80">
        <div className="space-y-2 sm:space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{
                  duration: 1,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "linear",
                }}
              >
                <Icon className="text-muted-foreground h-5 w-5 sm:h-6 sm:w-6" icon="mdi:loading" />
              </motion.div>
            </div>
          ) : workspaces?.data && workspaces.data.length > 0 ? (
            workspaces.data.map((workspace, index) => (
              <motion.div
                key={workspace.id}
                animate={{ opacity: 1, scale: 1 }}
                className={`group bg-muted/20 hover:bg-muted/30 relative cursor-pointer overflow-hidden rounded-lg p-3 transition-all duration-300 sm:rounded-xl sm:p-4 ${
                  workspaceId === workspace.id
                    ? "border-l-2 border-l-green-500 bg-green-500/10 sm:border-l-4"
                    : "border-l-2 border-l-transparent sm:border-l-4"
                }`}
                initial={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                onClick={() => handleWorkspaceClick(workspace.id)}
              >
                <div className="absolute inset-0 top-1/2 -left-0 h-24 w-24 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full bg-gradient-to-br from-green-400 to-blue-500 opacity-25 sm:h-32 sm:w-32">
                  <img
                    alt={workspace.name}
                    className="h-full w-full object-cover blur-2xl"
                    src={workspace.logo || "/placeholder.svg?height=40&width=40"}
                  />
                </div>
                <div className="flex items-center gap-2.5 sm:gap-3">
                  <div className="bg-muted/30 flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-md sm:h-10 sm:w-10 sm:rounded-lg">
                    <img
                      alt={workspace.name}
                      className="h-full w-full object-cover"
                      src={workspace.logo || "/placeholder.svg?height=40&width=40"}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-accent-foreground truncate text-xs font-medium sm:text-sm">
                      {workspace.name}
                    </h4>
                    <p className="text-muted-foreground text-[0.65rem] sm:text-xs">
                      <span className="capitalize">
                        {t(`workspaces:workspaceType.${workspace.workspace_type}`)}
                      </span>{" "}
                      • {t("workspaces:membersCount", { count: workspace.users_count })}
                    </p>
                  </div>
                  <Icon
                    className="text-muted-foreground group-hover:text-accent-foreground h-3.5 w-3.5 flex-shrink-0 transition-colors sm:h-4 sm:w-4"
                    icon="mdi:chevron-right"
                  />
                </div>
              </motion.div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center sm:py-8">
              <Icon
                className="text-muted-foreground mb-2 h-6 w-6 sm:mb-3 sm:h-8 sm:w-8"
                icon="mdi:folder-open-outline"
              />
              <p className="text-muted-foreground text-xs sm:text-sm">
                {t("workspaces:noWorkspacesAvailable")}
              </p>
            </div>
          )}
        </div>
      </ScrollArea>
    </motion.div>
  );
};
